import { Flex } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign } from '@feathr/blackbox';
import { GEOFILTER_SET_NAME_MAX_LENGTH } from '@feathr/blackbox';
import { Button, ButtonValid, EmptyState, Input, Modal, Text, Toggle } from '@feathr/components';
import { cssVar, getIconForAction } from '@feathr/hooks';

import GeoFilter from '../GeoFilter';
import type { TGeoFilterModalContext } from '../Locations.useModals';
import SaveChangesModal from '../SaveChangesModal';
import type { IGeoFilterSetData } from './GeoFilterModal.hooks';
import { useGeoFilterModalForm } from './GeoFilterModal.hooks';
import { getModalTitle, getSubmitButtonLabel, getValidationErrors } from './GeoFilterModal.utils';

import * as styles from './GeoFilterModal.css';

interface IGeoFilterModalProps {
  opened: boolean;
  onClose: () => void;
  context?: TGeoFilterModalContext;
  onSaveChanges?: (updatedData: IGeoFilterSetData) => Promise<void>;
  initialData?: IGeoFilterSetData;
  campaign?: Campaign;
}

function GeoFilterModal({
  opened: isModalOpen,
  onClose: closeModal,
  context = 'create',
  onSaveChanges,
  initialData,
  campaign,
}: Readonly<IGeoFilterModalProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    geoFilterSetName,
    setGeoFilterSetName,
    isDefault,
    setIsDefault,
    isSaving,
    isDirty,
    ephemeralTargetings,
    saveChanges,
    handleApply,
    handleSaveChangesSubmit,
    filteredTargetings,
    remainingLocations,
    hasInvalidTargetings,
    addFilterErrors,
    handleAddFilter,
    handleRemoveFilter,
    nameValidationErrors,
  } = useGeoFilterModalForm({
    isModalOpen,
    initialData,
    campaign,
    context,
    onSaveChanges,
    closeModal,
  });

  const saveErrors = getValidationErrors(
    context,
    geoFilterSetName,
    filteredTargetings,
    hasInvalidTargetings,
    t,
  );

  return (
    <Modal
      bodyClassName={styles.body}
      closeOnClickOutside={false}
      description={t('{{count}} locations remaining.', { count: remainingLocations })}
      leftActions={[
        <ButtonValid
          errors={addFilterErrors}
          id={'add-filter'}
          key={'add-filter'}
          onClick={handleAddFilter}
          prefix={getIconForAction('add')}
          type={'secondary'}
        >
          {t('Add location')}
        </ButtonValid>,
      ]}
      onClose={closeModal}
      opened={isModalOpen}
      rightActions={[
        <Button disabled={isSaving} key={'cancel'} onClick={closeModal}>
          {t('Cancel')}
        </Button>,
        <ButtonValid
          disabled={saveErrors.length > 0 || isSaving || !isDirty}
          errors={saveErrors}
          id={'save'}
          isLoading={isSaving}
          key={'save'}
          onClick={handleApply}
        >
          {getSubmitButtonLabel(context, t)}
        </ButtonValid>,
      ]}
      size={'lg'}
      title={getModalTitle(context, t)}
    >
      <Flex direction={'column'} gap={cssVar('--spacing-4')} w={'100%'}>
        <Input
          disabled={isSaving}
          label={t('Geofilter set name')}
          maxLength={GEOFILTER_SET_NAME_MAX_LENGTH}
          onChange={(value): void => setGeoFilterSetName(value || '')}
          placeholder={t('Geofilter set name')}
          validationError={nameValidationErrors}
          value={geoFilterSetName}
        />
        <Toggle
          label={t('Assign as default set')}
          layout={'well'}
          onChange={(newValue): void => setIsDefault(newValue ?? false)}
          value={isDefault}
        />

        <Flex direction={'column'} gap={cssVar('--spacing-3')}>
          <Text.DefaultSemibold>{t('Locations')}</Text.DefaultSemibold>

          {filteredTargetings.length === 0 ? (
            <EmptyState
              description={t('Add a filter to get started')}
              label={t('No filters added')}
              theme={'neutral'}
            />
          ) : (
            filteredTargetings.map((targeting, index) => (
              <GeoFilter
                campaign={campaign}
                key={targeting.id || index}
                layout={'inline'}
                onRemove={handleRemoveFilter}
                targeting={targeting}
                targetings={ephemeralTargetings}
              />
            ))
          )}
        </Flex>
      </Flex>

      {context === 'edit' && (
        <SaveChangesModal
          isLoading={isSaving}
          onClose={saveChanges.close}
          onSaveAndClose={handleSaveChangesSubmit}
          opened={saveChanges.isOpen}
        />
      )}
    </Modal>
  );
}

export default observer(GeoFilterModal);
